import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { clampToArea, defaultPlacement } from '@/shared/canvas/geometry';
import { garmentOf, zoneAspect, type GarmentType } from '@/shared/content/garments';
import type { Layer, Placement, PrintAsset, ProductTemplate, Side } from '@/shared/canvas/types';

const HISTORY_LIMIT = 30;

interface DesignState {
  template: ProductTemplate | null;
  layers: Layer[];
  activeLayerId: string | null;
  history: Layer[][];

  setTemplate: (template: ProductTemplate) => void;
  addPrint: (print: PrintAsset, side: Side) => void;
  removeLayer: (id: string) => void;
  moveToSide: (id: string, side: Side) => void;
  /** Тип изделия: от него зависят пропорции зоны печати. */
  garmentType: GarmentType;
  setGarmentType: (type: GarmentType) => void;
  setActive: (id: string | null) => void;
  updatePlacement: (id: string, placement: Placement, commit?: boolean) => void;
  flipLayer: (id: string) => void;
  fitToAspect: (id: string, aspect: number) => void;
  reorder: (id: string, direction: 'up' | 'down') => void;
  undo: () => void;
  reset: () => void;
  startNew: () => void;
  layersOf: (side: Side) => Layer[];
}

/**
 * Шаблон по умолчанию — белая футболка из макета.
 * Заменяется данными партнёра, как только они появятся в базе.
 */
const DEFAULT_TEMPLATE: ProductTemplate = {
  productId: 'demo-tshirt',
  color: 'black',
  images: {
    front: '/demo/tshirt-black-front.png',
    back: '/demo/tshirt-black-back.png',
  },
  areas: {
    front: {
      side: 'front',
      widthMm: 280,
      heightMm: 380,
      box: { x: 0.3, y: 0.22, width: 0.4, height: 0.46 },
    },
    back: {
      side: 'back',
      widthMm: 280,
      heightMm: 380,
      box: { x: 0.3, y: 0.2, width: 0.4, height: 0.46 },
    },
  },
};

export const useDesignStore = create<DesignState>()(
  persist(
    (set, get) => ({
      template: DEFAULT_TEMPLATE,
      layers: [],
      activeLayerId: null,
      history: [],
      garmentType: 'tshirt',

      setTemplate: (template) => set({ template }),

      addPrint: (print, side) => {
        if (!get().template) return;

        // Рамка повторяет пропорции картинки, поэтому вокруг принта нет пустых
        // полей и он двигается вплотную к краям зоны.
        const aspect = print.sourceWidth / print.sourceHeight;
        const area = zoneAspect(garmentOf(get().garmentType));

        const layer: Layer = {
          id: `${print.id}-${Date.now()}`,
          side,
          print,
          placement: defaultPlacement(aspect, area),
        };

        pushHistory(set, get);
        set((s) => ({ layers: [...s.layers, layer], activeLayerId: layer.id }));
      },

      removeLayer: (id) => {
        pushHistory(set, get);
        set((s) => ({
          layers: s.layers.filter((l) => l.id !== id),
          activeLayerId: s.activeLayerId === id ? null : s.activeLayerId,
        }));
      },

      moveToSide: (id, side) =>
        set((s) => ({ layers: s.layers.map((l) => (l.id === id ? { ...l, side } : l)) })),

      // Смена изделия переносит принты на новую зону, а не стирает их.
      //
      // Раньше здесь стоял сброс, и любой заход на шаг основы с другим
      // изделием оставлял человека с пустой вещью: он переключался в
      // шапке, а принты исчезали без объяснений. Координаты и правда
      // означали бы другое место, поэтому каждый принт возвращается
      // в середину новой зоны — с сохранением своих пропорций.
      setGarmentType: (type) => {
        if (get().garmentType === type) return;

        const area = zoneAspect(garmentOf(type));
        set((s) => ({
          garmentType: type,
          history: [],
          layers: s.layers.map((layer) => ({
            ...layer,
            placement: {
              ...defaultPlacement(layer.print.sourceWidth / layer.print.sourceHeight, area),
              rotation: layer.placement.rotation,
              flipped: layer.placement.flipped,
            },
          })),
        }));
      },

      setActive: (id) => set({ activeLayerId: id }),

      // commit=false во время перетаскивания: иначе история забьётся сотнями шагов
      updatePlacement: (id, placement, commit = false) => {
        if (commit) pushHistory(set, get);
        set((s) => ({
          layers: s.layers.map((l) =>
            l.id === id ? { ...l, placement: clampToArea(placement) } : l,
          ),
        }));
      },

      // Отражение по горизонтали. Положение и размер не меняются: человек
      // переворачивает рисунок, а не двигает его.
      flipLayer: (id) => {
        pushHistory(set, get);
        set((s) => ({
          layers: s.layers.map((layer) =>
            layer.id === id
              ? { ...layer, placement: { ...layer.placement, flipped: !layer.placement.flipped } }
              : layer,
          ),
        }));
      },

      // Пропорции из каталога могут разойтись с файлом. Когда картинка
      // загрузилась, приводим рамку к её настоящим пропорциям: центр остаётся
      // на месте, ширина сохраняется.
      fitToAspect: (id, aspect) => {
        const layer = get().layers.find((item) => item.id === id);
        if (!layer || !Number.isFinite(aspect) || aspect <= 0) return;

        const current = layer.placement;
        const height = (current.width / aspect) * zoneAspect(garmentOf(get().garmentType));
        if (Math.abs(height - current.height) < 0.005) return;

        const centerY = current.y + current.height / 2;
        set((s) => ({
          layers: s.layers.map((item) =>
            item.id === id
              ? {
                  ...item,
                  placement: clampToArea({
                    ...current,
                    height,
                    y: centerY - height / 2,
                  }),
                }
              : item,
          ),
        }));
      },

      reorder: (id, direction) => {
        const layers = [...get().layers];
        const index = layers.findIndex((l) => l.id === id);
        const target = direction === 'up' ? index + 1 : index - 1;
        if (index < 0 || target < 0 || target >= layers.length) return;

        const moved = layers[index];
        const other = layers[target];
        if (!moved || !other) return;

        pushHistory(set, get);
        layers[index] = other;
        layers[target] = moved;
        set({ layers });
      },

      undo: () => {
        const history = [...get().history];
        const previous = history.pop();
        if (!previous) return;
        set({ layers: previous, history, activeLayerId: null });
      },

      // Убирает принты, но оставляет историю: это шаг внутри работы над вещью,
      // и «отменить» после него обязано вернуть раскладку.
      reset: () => {
        pushHistory(set, get);
        set({ layers: [], activeLayerId: null });
      },

      // Начало новой вещи. В отличие от reset стирает и историю:
      // изделие уехало в корзину и живёт там своей копией раскладки, а
      // конструктор должен открыться чистым. Иначе следующая футболка приходит
      // с чужим принтом на том же месте, и человек видит его как свой.
      startNew: () => set({ layers: [], activeLayerId: null, history: [] }),

      layersOf: (side) => get().layers.filter((l) => l.side === side),
    }),
    {
      name: 'ls-design',
      // История отмен — состояние сеанса: восстанавливать её после
      // перезагрузки бессмысленно, отменять человеку уже нечего.
      partialize: (state) => ({
        layers: state.layers,
        garmentType: state.garmentType,
      }),
    },
  ),
);

function pushHistory(set: (partial: Partial<DesignState>) => void, get: () => DesignState): void {
  const history = [...get().history, get().layers].slice(-HISTORY_LIMIT);
  set({ history });
}
